import { LineCap, LineJoin, SvgGraphics } from '@tencent/alloy-renderer';
import { DrawAttrs } from '@tencent/chart/lib/antv/g2/interface';
import { convertColor, isEmpty } from './common';
import { convertFrame, convertGradual } from './style';
import { IStrokeStyle, IFillStyle } from '@tencent/alloy-graphics';
import { Stroke } from '@tencent/alloy-renderer-adapter';

export const LINE_JOIN_MAP = {
    miter: LineJoin.Miter,
    bevel: LineJoin.Bevel,
    round: LineJoin.Round,
};

export const LINE_CAP_MAP = {
    butt: LineCap.Butt,
    round: LineCap.Round,
    square: LineCap.Square,
};

export function transLineJoin(lineJoin: 'bevel' | 'round' | 'miter'): LineJoin {
    return LINE_JOIN_MAP[lineJoin];
}

export function transLineCap(lineCap: 'butt' | 'round' | 'square'): LineCap {
    return LINE_CAP_MAP[lineCap];
}

/**
 * g2绘制参数转alloy/render绘制参数。
 * 注意：这里由于strokeStyle不直接支持lineDash，返回的结果会漏掉strokeType，设置element样式时需要设置lineDash
 * @param drawAttrs
 */
function convertLineAttrsToStrokeStyle(drawAttrs: DrawAttrs): IStrokeStyle {
    const { stroke, strokeOpacity, lineWidth, lineJoin, lineCap } = drawAttrs;
    let fillStyle: IFillStyle;
    if (!stroke || stroke === 'transparent') {
        fillStyle = { noFill: true };
    } else if (stroke.startsWith('#')) { // 纯色
        const color = convertColor(stroke, strokeOpacity);
        fillStyle = { solidFill: { color } };
    } else {
        const gradual = convertGradual(stroke);
        fillStyle = { gradFill: gradual };
    }
    return {
        lineWidth: lineWidth || 0,
        lineCap: lineCap && transLineCap(lineCap),
        lineJoin: lineJoin && transLineJoin(lineJoin),
        fillStyle,
    };
}

/**
 * 二次加工传入的strokeStyle
 * @param stroke
 */
export function handleStrokeStyle(stroke: IStrokeStyle): IStrokeStyle {
    const strokeStyle = stroke;
    if (strokeStyle?.fillStyle?.gradFill) {  // 必须rotateWithShape设置为false，否则渐变效果不对
        strokeStyle.fillStyle.gradFill.rotateWithShape = false;
    }
    if (strokeStyle?.fillStyle?.imageFill) {
        strokeStyle.fillStyle.imageFill.rotateWithShape = false;
    }
    return strokeStyle ?? { fillStyle: { noFill: true } };
}

/**
 * 设置统一渲染层graphic元素边框
 * @param graphics
 * @param drawAttrs
 */
export function setCustomStrokeStyle(graphics: SvgGraphics, drawAttrs: DrawAttrs) {
    const element = graphics;
    const { strokeStyle } = drawAttrs.customStyles || { };
    let renderStrokeStyle: IStrokeStyle | undefined;
    if (isEmpty(strokeStyle)) {  // strokeStyle为透传的原始数据
        renderStrokeStyle = convertLineAttrsToStrokeStyle(drawAttrs);
    } else {
        renderStrokeStyle = handleStrokeStyle(strokeStyle as IStrokeStyle);
    }
    if (!renderStrokeStyle) return;
    const lineWidth = renderStrokeStyle.lineWidth || 0;
    const stroke = new Stroke(
        renderStrokeStyle,
        { scaleX: 1, scaleY: 1 }, convertFrame(element, lineWidth),
    );
    element.strokeWidth = stroke.strokeWidth;
    element.stroke = stroke.strokeFill;
    element.strokeLineCap = stroke.lineCap;
    element.strokeLineJoin = stroke.lineJoin;
    const { lineDash } = stroke;
    element.strokeDash = lineDash;

    if (!lineDash?.length && drawAttrs.lineDash) {  // 这里做一个兜底
        element.strokeDash = drawAttrs.lineDash;
    }
}
